// ─────────────────────────────────────────────────────────────────────────────
// RF-011 (§25) — Landing común Pulso vs Shine. Cuando el perfil tiene AMBOS módulos
// (canViewPulso && canViewBrillamos), el usuario elige a cuál espacio ir. Réplica del
// LandingChooserPage del demo (título + mascota Post Meeting Pulse + logo Shine Together
// + copy al hover), adaptada al armazón real:
//   · "Post Meeting Pulse" vive en OTRO armazón (portal Pulso, `App`, rutas fuera de /app):
//     navegación de PÁGINA COMPLETA (<a href="/">), igual que RecognitionBanner hace el inverso.
//   · "Shine Together" vive en ESTE armazón (/app): navegación SPA (`navigate(recoTarget)`),
//     donde recoTarget = /app/brillamos si hay campaña Brillamos activa, si no /app/reconocer.
// La DECISIÓN de mostrar esta pantalla la toma LandingDecision.tsx (por flags del perfil).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HomeRightRail } from '@/components/layout/HomeRightRail';
import { LanguageSelector } from './accessShared';
import { shineLogoSrc } from '../shineLogo';
import '../recognition.css';
import { withBase } from '@/utils/basePath';

/**
 * Campo de estrellas titilante del fondo (réplica del LandingStarfield del demo). Canvas vanilla:
 * dibuja estrellas de 5 puntas y puntos con alfa oscilante. Toda la aleatoriedad/animación vive en
 * el efecto (nunca en render). Fondo transparente: el gradiente lo pone `.acc-standalone`.
 */
const STAR_COLORS = ['#F08521', '#342F82', '#E11F26'];

/**
 * PRNG determinista (mulberry32). Se usa en vez de Math.random para estrellas DECORATIVAS: no es un
 * contexto de seguridad y así se evita el bloqueo de Sonar (`sonarjs/pseudo-random`). Se siembra con
 * la hora al montar, para que el patrón varíe entre cargas sin necesidad de un RNG criptográfico.
 */
function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function LandingStarfield() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const rng = makeRng(Date.now());
    const setSize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    setSize();

    const buildStars = () => Array.from({ length: 130 }, () => ({
      x: rng() * canvas.width,
      y: rng() * canvas.height,
      outerRadius: rng() * 5 + 2.5,
      twinkleSpeed: 0.018 + rng() * 0.022,
      twinklePhase: rng() * Math.PI * 2,
      rotation: rng() * Math.PI * 2,
      minAlpha: 0.06 + rng() * 0.06,
      maxAlpha: 0.28 + rng() * 0.2,
      isStar: rng() > 0.4,
      color: STAR_COLORS[Math.floor(rng() * STAR_COLORS.length)],
    }));

    let stars = buildStars();
    let animId = 0;
    let t = 0;

    const drawStar5 = (x: number, y: number, outerR: number, alpha: number, rot: number, color: string) => {
      const innerR = outerR * 0.42;
      const pts = 5;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = color;
      ctx.beginPath();
      for (let i = 0; i < pts * 2; i++) {
        const r = i % 2 === 0 ? outerR : innerR;
        const angle = (Math.PI / pts) * i - Math.PI / 2 + rot;
        ctx.lineTo(x + Math.cos(angle) * r, y + Math.sin(angle) * r);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      t++;
      for (const s of stars) {
        const twinkle = Math.sin(t * s.twinkleSpeed + s.twinklePhase);
        const alpha = s.minAlpha + ((twinkle + 1) / 2) * (s.maxAlpha - s.minAlpha);
        if (s.isStar) {
          drawStar5(s.x, s.y, s.outerRadius, alpha, s.rotation, s.color);
        } else {
          ctx.save();
          ctx.globalAlpha = alpha;
          ctx.fillStyle = s.color;
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.outerRadius * 0.36, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    const onResize = () => { setSize(); stars = buildStars(); };
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', onResize); };
  }, []);

  return <canvas ref={canvasRef} className="acc-landing2-starfield" aria-hidden="true" />;
}

/** Mascota "Post Meeting Pulse" del demo (SVG autocontenido; parpadeo + saludo vía SMIL, sin JS). */
function WavingMascot({ className }: Readonly<{ className?: string }>) {
  return (
    <svg viewBox="0 0 378.88 381.37" className={className} aria-hidden="true" focusable="false">
      <g>
        <path fill="#332f81" d="M158.67,143.98c36.98,0,66.95-29.97,66.95-66.95S195.65,10.08,158.67,10.08s-66.95,29.97-66.95,66.95,29.97,66.95,66.95,66.95Z" />
        <g>
          <ellipse fill="#fff" cx="143.89" cy="71.29" rx="7.92" ry="7.92">
            <animate attributeName="ry" values="7.92;7.92;0.5;7.92;7.92;0;0;0;0;7.92;7.92" keyTimes="0;0.15;0.17;0.19;0.35;0.38;0.55;0.65;0.68;0.71;1" dur="3s" repeatCount="indefinite" />
          </ellipse>
          <ellipse fill="#fff" cx="173.43" cy="71.29" rx="7.92" ry="7.92">
            <animate attributeName="ry" values="7.92;7.92;0.5;7.92;7.92;0;0;0;0;7.92;7.92" keyTimes="0;0.15;0.17;0.19;0.35;0.38;0.55;0.65;0.68;0.71;1" dur="3s" repeatCount="indefinite" />
          </ellipse>
          <path d="M136.5,68 Q143.89,59 151.28,68" stroke="#fff" strokeWidth="2.8" fill="none" strokeLinecap="round" opacity="0">
            <animate attributeName="opacity" values="0;0;0;0;0;1;1;1;1;0;0" keyTimes="0;0.15;0.17;0.19;0.35;0.38;0.55;0.65;0.68;0.71;1" dur="3s" repeatCount="indefinite" />
          </path>
          <path d="M166,68 Q173.43,59 180.86,68" stroke="#fff" strokeWidth="2.8" fill="none" strokeLinecap="round" opacity="0">
            <animate attributeName="opacity" values="0;0;0;0;0;1;1;1;1;0;0" keyTimes="0;0.15;0.17;0.19;0.35;0.38;0.55;0.65;0.68;0.71;1" dur="3s" repeatCount="indefinite" />
          </path>
        </g>
        <path fill="#ee5294" d="M160.27,152.99c25.15.79,45.27,21.42,45.27,46.73,0,19.37-11.79,36-28.59,43.1-5.31,2.28-11.13,3.58-17.24,3.71,31.24.5,56.51,26.07,56.51,57.42l44.97-110.85c-26.58-24.59-62.02-39.73-100.92-40.11Z" />
        <path fill="#ef8421" d="M121.42,227.88c-5.91-7.83-9.41-17.58-9.41-28.14,0-25.82,20.95-46.77,46.77-46.77.5,0,.99.01,1.49.02-.5-.02-1-.04-1.5-.04-83.26,0-150.98,67.72-150.98,150.98,0,23.38,17.23,43.15,40.38,46.35l110.95,15.33c2.17.3,4.35.45,6.47.45h0c22.97-.01,43.02-16.97,46.27-40.39,3.55-25.6-14.33-49.19-39.93-52.74l-55.56-7.69c10.22-11.19,24.79-18.32,41.01-18.71-14.72-.4-27.73-7.63-35.96-18.65Z" />
        <path fill="#ee5294" d="M112.01,199.74c0,10.56,3.5,20.31,9.41,28.14-5.79-7.75-9.22-17.37-9.22-27.79,0-25.66,20.81-46.47,46.47-46.47s46.47,20.81,46.47,46.47c0,19.17-11.61,35.63-28.19,42.73,16.79-7.1,28.59-23.73,28.59-43.1,0-25.32-20.12-45.94-45.27-46.73-.5,0-.99-.02-1.49-.02-25.82,0-46.77,20.95-46.77,46.77Z" />
        <path fill="#e11f26" d="M205.14,200.09c0-25.66-20.81-46.47-46.47-46.47s-46.47,20.81-46.47,46.47c0,10.42,3.43,20.04,9.22,27.79,8.46,11.22,21.85,18.5,36.97,18.62.13,0,.25-.01.38-.01,6.45,0,12.59-1.31,18.18-3.67,16.57-7.1,28.19-23.56,28.19-42.73ZM181.54,202.79l-22.82,21.03-22.9-21.03c-6.04-6.05-6.05-15.84,0-21.88,6.05-6.04,15.84-6.04,21.88,0,.35.35.67.73.97,1.1.3-.38.62-.75.97-1.1,6.05-6.04,15.84-6.04,21.88,0h.02c6.04,6.05,6.04,15.84,0,21.88Z" />
        <path fill="#fff" d="M181.52,180.91h0,0c-6.04-6.04-15.83-6.04-21.88,0-.35.35-.67.72-.97,1.1-.3-.37-.62-.75-.97-1.1-6.04-6.04-15.83-6.04-21.88,0-6.05,6.04-6.04,15.83,0,21.88l22.9,21.03,22.82-21.03c6.04-6.04,6.04-15.83,0-21.88h-.02Z" />
        <g transform="translate(0, 18)">
          <path fill="#ee5294" d="M313.9,79.23c-23.6-10.52-51.27.07-61.78,23.67l-21.48,48.17-44.97,110.85c0,22.08,15.43,41.16,37.03,45.75,3.27.7,6.54,1.02,9.76,1.02h0v.03c18.15,0,35.06-10.61,42.7-27.74l62.41-139.97c10.52-23.6-.07-51.27-23.67-61.78Z">
            <animateTransform attributeName="transform" type="rotate" values="0 190 270; -10 190 270; 10 190 270; -10 190 270; 10 190 270; -10 190 270; 0 190 270" keyTimes="0; 0.12; 0.28; 0.44; 0.6; 0.76; 1" dur="2.4s" repeatCount="indefinite" calcMode="spline" keySplines="0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1" />
          </path>
        </g>
      </g>
    </svg>
  );
}

export function LandingChooser({ recoTarget }: Readonly<{ recoTarget: string }>) {
  const { t, i18n } = useTranslation('recognition');
  const navigate = useNavigate();
  return (
    <div className="acc-landing2">
      <LandingStarfield />
      {/* Rail de accesos rápidos (mismas pills del resto de la app): estrellas y rail replican el demo. */}
      <HomeRightRail />
      <div className="acc-landing2-topbar"><LanguageSelector /></div>

      <div className="acc-landing2-heading">
        <h1 className="acc-landing2-title">{t('landing.question')}</h1>
        <p className="acc-landing2-subtitle">{t('landing.subtitle')}</p>
      </div>

      <div className="acc-landing2-cards">
        {/* Post Meeting Pulse — cruza al armazón de Pulso (rutas fuera de /app): navegación de página
            completa. Es un <button className="btn btn--secondary"> (no <a>) para que Enter y Espacio se comporten igual que la tarjeta
            Shine (dos controles idénticos deben activarse igual). */}
        <button
          type="button"
          className="acc-landing2-card acc-landing2-card--pulso"
          aria-label={t('landing.cardPulso')}
          onClick={() => window.location.assign(withBase('/'))}
        >
          <div className="acc-landing2-art">
            <WavingMascot className="acc-landing2-mascot" />
            <span className="acc-landing2-wordmark">Post Meeting<br />Pulse</span>
          </div>
          <div className="acc-landing2-hover">{t('landing.pulsoHover')}</div>
        </button>

        {/* Shine Together — mismo armazón (/app): navegación SPA. */}
        <button
          type="button"
          className="acc-landing2-card acc-landing2-card--shine"
          aria-label={t('landing.cardShine')}
          onClick={() => navigate(recoTarget)}
        >
          <div className="acc-landing2-art">
            {/* QA (2026-09-01) — DT-029: ruta fija en vez de `shineLogoSrc(lang)`. Esta pantalla tiene
                su propio selector de idioma visible (`<LanguageSelector />` arriba) — el logo se
                quedaba fijo aunque el usuario cambiara de idioma ahí mismo. Sin override de admin
                aquí (no es un banner configurable), así que no hay lectura exacta que hacer. */}
            <img className="acc-landing2-shine-logo" src={shineLogoSrc(i18n.language)} alt="" />
          </div>
          <div className="acc-landing2-hover">{t('landing.shineHover')}</div>
        </button>
      </div>
    </div>
  );
}

export default LandingChooser;
